import type { Category, ProductCategory } from '@scayle/storefront-nuxt'
import type { BreadcrumbItem } from '@scayle/storefront-core'
import { categoryLinkPath } from './category'

/**
 * Custom-data key (under `shopLevelCustomData`) that marks a category as a
 * default breadcrumb root for PDPs. Configured per category in the Scayle
 * backoffice (ECOM-2381) instead of being hardcoded as root ids.
 */
export const PDP_DEFAULT_BREADCRUMB_ROOT_ATTR = 'PDPIsDefaultBreadcrumbRoot'

/**
 * A product category path node enriched with the custom-data bags that SAPI
 * returns when the product `with`-params request them (PDPs request
 * `categories.shopLevelCustomData` + `countryLevelCustomData`).
 *
 * The base `ProductCategory` type does not declare these bags, so we extend it
 * here for typed access instead of casting through `any`.
 */
export type ProductCategoryNode = ProductCategory & {
  shopLevelCustomData?: Record<string, unknown>
  countryLevelCustomData?: Record<string, unknown>
}

/**
 * Whether a category path node is flagged as a default breadcrumb root.
 * Values come back as `unknown`, so we require an explicit `true`.
 */
export const isDefaultBreadcrumbRoot = (node?: ProductCategoryNode): boolean =>
  node?.shopLevelCustomData?.[PDP_DEFAULT_BREADCRUMB_ROOT_ATTR] === true

/**
 * Selects the PDP breadcrumb path from a product's category paths using the
 * data-driven default-root attribute (ECOM-2381).
 *
 * Algorithm:
 * - Consider only paths that contain at least one node flagged as a default root.
 * - Within a path, the default root is the SHALLOWEST flagged node (closest to
 *   the actual root) — this maximizes the default-root → leaf length.
 * - Slice each candidate path to start at its default root.
 * - Pick the candidate whose sliced length (default root → leaf) is greatest.
 * - On ties, keep the first candidate (stable SAPI order).
 *
 * Returns the sliced path, or `null` when no path carries the attribute — in
 * which case callers fall back to legacy root-id selection.
 */
export const selectDefaultBreadcrumbRootPath = (
  categories: ProductCategoryNode[][],
): ProductCategoryNode[] | null => {
  let best: ProductCategoryNode[] | null = null

  for (const path of categories) {
    const rootIndex = path.findIndex(isDefaultBreadcrumbRoot)
    if (rootIndex === -1) {
      continue
    }

    const sliced = path.slice(rootIndex)
    if (!best || sliced.length > best.length) {
      best = sliced
    }
  }

  return best
}

/**
 * Builds PLP breadcrumbs from a Scayle category tree, honoring a category's
 * `cmsPagePath` replacement (ECOM-2384).
 *
 * Mirrors Scayle's `getBreadcrumbsFromCategory` traversal (push a node when it
 * has children or is the active node, then recurse) but resolves each crumb's
 * `to` via the shared `categoryLinkPath` rule instead of the raw `category.path`
 * — so a category "replaced" by a content page links to that page, exactly like
 * the main/mobile/left-hand navigation does. The `to` stays raw (unlocalized);
 * the breadcrumb components localize it via `toLocalePath`.
 */
export const getCategoryBreadcrumbs = (
  category: Category,
  activeNode?: Category,
): BreadcrumbItem[] => {
  if (!category) {
    return []
  }

  const items: BreadcrumbItem[] = []

  const walk = (node: Category) => {
    if (node.children?.length || activeNode?.id === node.id) {
      items.push({ value: node.name, to: categoryLinkPath(node) })
      node.children?.forEach(walk)
    }
  }

  walk(category)
  return items
}

/**
 * Builds PDP breadcrumbs from a product's selected category path, honoring a
 * category's `cmsPagePath` replacement (ECOM-2384).
 *
 * Mirrors Scayle's `getBreadcrumbsFromProductCategories` (label from
 * `categoryName`) but resolves each crumb's `to` to
 * `countryLevelCustomData.cmsPagePath` when present, otherwise the raw
 * `categoryUrl`. Product category nodes are a different shape than PLP
 * `Category` (they expose `categoryUrl`, not `path`), so the fallback is applied
 * here rather than through `categoryLinkPath`.
 */
export const getProductCategoryBreadcrumbs = (
  path: ProductCategoryNode[],
): BreadcrumbItem[] =>
  (path ?? []).map((node) => {
    const cmsPagePath = node.countryLevelCustomData?.cmsPagePath

    return {
      value: node.categoryName,
      to: cmsPagePath ? (cmsPagePath as string) : node.categoryUrl,
    }
  })

/**
 * Whether a product category path starts with the given context category ids
 * (id-prefix match). The path must be at least as long as the context and every
 * context id must equal the path node's `categoryId` at the same index.
 */
const pathStartsWithContext = (
  path: ProductCategoryNode[],
  contextIds: number[],
): boolean =>
  path.length >= contextIds.length &&
  contextIds.every((id, index) => path[index]?.categoryId === id)

/**
 * Selects the PDP breadcrumb path using the customer's in-app navigation context
 * (ECOM-2383): the category path of the PLP the customer navigated from.
 *
 * Algorithm:
 * - Consider only the product paths that START WITH `contextIds` (the PLP's
 *   root→leaf category id chain) — i.e. the product path that fits the PLP the
 *   product was opened from.
 * - Pick the longest fitting path (most nodes from root to leaf).
 * - On ties, keep the first candidate (stable SAPI order).
 *
 * Returns the full fitting path (root → leaf), or `null` when there is no context
 * or no path fits — in which case callers fall back to the ECOM-2381 selection.
 */
export const selectNavigationContextPath = (
  categories: ProductCategoryNode[][],
  contextIds: number[],
): ProductCategoryNode[] | null => {
  if (!contextIds.length) {
    return null
  }

  let best: ProductCategoryNode[] | null = null

  for (const path of categories) {
    if (!pathStartsWithContext(path, contextIds)) {
      continue
    }

    if (!best || path.length > best.length) {
      best = path
    }
  }

  return best
}
